import { useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
  Polyline,
} from "react-leaflet";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  MapPin,
  Search,
  Navigation,
  Clock,
  ChevronDown,
  Loader2,
  Star,
  Phone,
} from "lucide-react";
import { BranchDto, RestaurantWithBranchesDto } from "@/types";
import L from "leaflet";
import { useApp } from "@/App";
import { formatCanadianPhoneNumber } from "@/services/utlits";

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

interface MapLocationSelectorProps {
  selectedLocation: BranchDto | null;
  onLocationSelect: (location: BranchDto) => void;
}

interface UserLocation {
  lat: number;
  lng: number;
}

function LocationMarker({
  onLocationSelect,
}: {
  onLocationSelect: (lat: number, lng: number) => void;
}) {
  const [position, setPosition] = useState<[number, number] | null>(null);

  const map = useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      setPosition([lat, lng]);
      onLocationSelect(lat, lng);
    },
  });

  return position === null ? null : (
    <Marker position={position}>
      <Popup>Selected location</Popup>
    </Marker>
  );
}

export function MapLocationSelector({
  selectedLocation,
  onLocationSelect,
}: MapLocationSelectorProps) {
  const { restaurantWithBranches } = useApp();
  const [branchesWithDistance, setBranchesWithDistance] = useState<BranchDto[]>(
    []
  );

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>([
    56.1304, -106.3468,
  ]); // Default: Canada

  useEffect(() => {
    if (restaurantWithBranches && restaurantWithBranches?.branches.length > 0) {
      setBranchesWithDistance(restaurantWithBranches.branches);
      const firstBranch = restaurantWithBranches.branches[0];
      if (firstBranch.latitude && firstBranch.longitude) {
        setMapCenter([firstBranch.latitude, firstBranch.longitude]);
      }
    }
  }, [restaurantWithBranches]);

  const getCurrentLocation = () => {
    setIsLoadingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setMapCenter([latitude, longitude]);
          setIsLoadingLocation(false);
        },
        (error) => {
          console.error("Error getting location:", error);
          setIsLoadingLocation(false);
        }
      );
    } else {
      setIsLoadingLocation(false);
    }
  };
  function calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const toRad = (value: number) => (value * Math.PI) / 180;

    const R = 6371; // Radius of Earth in km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  }

  const handleMapLocationSelect = (lat: number, lng: number) => {
    // In a real app, you'd reverse geocode this to get address details
    const newLocation: BranchDto = {
      id: 1,
      name: "Custom Location",
      addressLine1: `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
      distance: 0.0,
      isOpen: true,
    };
    onLocationSelect(newLocation);
  };
  useEffect(() => {
    if (
      !userLocation ||
      !restaurantWithBranches?.branches ||
      restaurantWithBranches.branches.length === 0
    )
      return;

    const updatedBranches = restaurantWithBranches.branches.map(
      (branch: BranchDto) => {
        if (branch.latitude !== undefined && branch.longitude !== undefined) {
          const dist = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            branch.latitude,
            branch.longitude
          );
          return { ...branch, distance: Math.round(dist * 10) / 10 }; // rounded to 1 decimal
        }
        return branch;
      }
    );
    setBranchesWithDistance(updatedBranches);
  }, [userLocation, restaurantWithBranches]);

  const filteredLocations = branchesWithDistance?.filter(
    (location: BranchDto) =>
      location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.addressLine1.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="justify-between w-full h-14 sm:h-16 bg-white hover:bg-gray-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 group"
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
              <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="text-left min-w-0 flex-1">
              {selectedLocation ? (
                <>
                  <div className="font-semibold text-gray-900 text-sm sm:text-base break-words whitespace-normal">
                    {selectedLocation.name}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 flex items-center space-x-2">
                    {selectedLocation.distance !== undefined
                      ? `${selectedLocation.distance} km away`
                      : "0 km away"}
                    <span>•</span>
                    <span
                      className={
                        selectedLocation.isOpen
                          ? "text-green-600"
                          : "text-red-600"
                      }
                    >
                      {selectedLocation.isOpen ? "Open" : "Closed"}
                    </span>
                  </div>
                </>
              ) : (
                <span className="text-sm sm:text-base text-gray-600">
                  Select Location
                </span>
              )}
            </div>
          </div>
          <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-emerald-600 transition-colors duration-200" />
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[95vw] max-w-5xl mx-auto p-4 sm:p-6 max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-3 text-lg sm:text-xl">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
              <MapPin className="w-4 h-4 text-white" />
            </div>
            <span className="font-display">Choose Your Location</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 h-[500px] sm:h-[600px]">
          {/* Left Panel - Location List */}
          <div className="space-y-3 sm:space-y-4">
            {/* Search and Current Location */}
            <div className="space-y-3">
              {restaurantWithBranches &&
                restaurantWithBranches?.branches?.length > 1 && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search locations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-12 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                    />
                  </div>
                )}

              <Button
                onClick={getCurrentLocation}
                disabled={isLoadingLocation}
                className="w-full h-12 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {isLoadingLocation ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Navigation className="w-4 h-4 mr-2" />
                )}
                Use Current Location
              </Button>
            </div>

            {/* Location List */}
            <div className="space-y-3 max-h-[350px] sm:max-h-[480px] overflow-y-auto custom-scrollbar">
              {filteredLocations?.map((location) => (
                <div
                  key={location.id}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                    selectedLocation?.id === location.id
                      ? "border-emerald-500 bg-emerald-50 shadow-lg transform scale-[1.02]"
                      : "border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50"
                  }`}
                  onClick={() => {
                    onLocationSelect(location);
                    setIsOpen(false);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-sm sm:text-base">
                            {location?.name}
                          </h3>
                          <div className="flex items-center space-x-2 mt-1">
                            {location.isOpen ? (
                              <Badge className="bg-green-100 text-green-800 text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                Open
                              </Badge>
                            ) : (
                              <Badge
                                variant="secondary"
                                className="bg-red-100 text-red-800 text-xs"
                              >
                                Closed
                              </Badge>
                            )}
                            {/* <div className="flex items-center space-x-1">
                              <Star className="w-3 h-3 text-yellow-500 fill-current" />
                              <span className="text-xs text-gray-600">4.8</span>
                            </div> */}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <p className="text-xs sm:text-sm text-gray-600 flex items-center space-x-1">
                          <MapPin className="w-3 h-3" />
                          <span>{location.addressLine1}</span>
                        </p>
                        <div className="flex items-center justify-between">
                          <p className="text-xs sm:text-sm text-emerald-600 font-semibold flex items-center space-x-1">
                            <Navigation className="w-3 h-3" />
                            <span>
                              {location.distance !== undefined
                                ? `${location.distance} km away`
                                : "0 km away"}
                            </span>
                          </p>
                          {location?.phoneNumber && (
                            <div className="flex items-center space-x-1 text-xs text-gray-500">
                              <Phone className="w-3 h-3" />
                              <span>
                                {formatCanadianPhoneNumber(
                                  restaurantWithBranches?.phoneNumber || ""
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Panel - Map */}
          <div className="rounded-xl overflow-hidden border border-gray-200 hidden xl:block">
            <MapContainer
              center={mapCenter}
              zoom={5}
              style={{ height: "100%", width: "100%" }}
              className="z-0"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {/* Restaurant Locations */}
              {branchesWithDistance?.map((location) => (
                <Marker
                  key={location.id}
                  position={[
                    location?.latitude ?? 34.0522 + Math.random() * 0.1,
                    location?.longitude ?? -118.2437 + Math.random() * 0.1,
                  ]}
                >
                  <Popup>
                    <div className="p-3 min-w-[200px]">
                      <h3 className="font-bold text-sm mb-2">
                        {restaurantWithBranches?.name} {" - "} {location?.name}
                      </h3>
                      <p className="text-xs text-gray-600 mb-2">
                        {location.addressLine1}
                      </p>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs text-emerald-600 font-semibold">
                          {location.distance !== undefined
                            ? `${location.distance} km away`
                            : "0 km away"}
                        </p>
                        <Badge
                          className={
                            location.isOpen
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }
                        >
                          {location.isOpen ? "Open" : "Closed"}
                        </Badge>
                      </div>
                      <Button
                        size="sm"
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => {
                          onLocationSelect(location);
                          setIsOpen(false);
                        }}
                      >
                        Select This Location
                      </Button>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* User Location */}
              {userLocation && (
                <Marker position={[userLocation.lat, userLocation.lng]}>
                  <Popup>Your current location</Popup>
                </Marker>
              )}
              {userLocation &&
                selectedLocation &&
                selectedLocation.latitude !== undefined &&
                selectedLocation.longitude !== undefined && (
                  <Polyline
                    positions={[
                      [userLocation.lat, userLocation.lng],
                      [selectedLocation.latitude, selectedLocation.longitude],
                    ]}
                    pathOptions={{
                      color: "blue",
                      weight: 3,
                      opacity: 0.8,
                      dashArray: "2, 10", // 2px line, 10px gap (dotted feel)
                    }}
                  />
                )}

              {/* Click to select location */}
              <LocationMarker onLocationSelect={handleMapLocationSelect} />
            </MapContainer>
          </div>
        </div>

        {/* Mobile Map Toggle */}
        <div className="xl:hidden">
          <Button
            variant="outline"
            className="w-full h-12 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            onClick={() => {
              // In a real app, this would open a full-screen map
              alert("Full-screen map would open here on mobile");
            }}
          >
            <MapPin className="w-4 h-4 mr-2" />
            View on Map
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
